/* eslint-disable max-lines */
/* eslint-disable max-len */
/* eslint-disable id-length */
/* eslint-disable max-lines-per-function */
import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { useAuth } from '../../../Hooks/useAuth';
import { appendServiceNameToURL, appendServicePortToURL } from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import './aiAccessReq.scss';
import { AI_ACCESS_REQ } from '../../../Api/resources/endPoint';
import {
  fetchDropDownOptions as fetchAiAccessDropDownOptions,
  addAiAccessRequest,
  deleteAiAccessRequest,
  getAiAccessRequestById,
  updateAiAccessRequest,
} from '../../../Api/request/aiAccessReq';
import AddAiAccessReqModal from './modals/AddAiAccessReqModal';
import EditAiAccessReqModal from './modals/EditAiAccessReqModal';
import ApprovalStatusModal, { getApprovalDialog } from './modals/ApprovalStatusModal';
import ActionConfirmModal from '../../Project/TimeSheetReport/ActionConfirmModal';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { toast } from 'react-toastify';
import { MODULE_NAMES } from '../../../Constants/modules';
import dayjs from 'dayjs';

const PAGE_SIZE = 10;

// Build service URL once (module level, no re-runs)
const buildServiceURL = (service, path) => {
  const url = appendServiceNameToURL(service);
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL + url + path;
  }
  const port = appendServicePortToURL(service);
  const baseUrl = process.env.REACT_APP_API_URL.replace(/\/$/, '');
  return `${baseUrl}:${port}${url}${path}`;
};
const buildExportFileName = (fileName) => {
  const timestamp = dayjs().format('DD-MMM-YYYY-hh-mm-ss-A');
  return `${fileName}-${timestamp}.xlsx`;
};

const buildExportSheetName = (sheetName) => {
  return sheetName;
};

const baseURL = buildServiceURL(serviceName.LANDING_MS, '/api' + AI_ACCESS_REQ);
const getOptionId = (option) => {
  if (option === null || option === undefined) return undefined;
  if (typeof option !== 'object') return option;
  return option.value
    ?? option.id
    ?? option.employeeId
    ?? option.employee_id
    ?? option.projectId
    ?? option.project_id
    ?? option.project_seq_id
    ?? option.requestTypeId
    ?? option.request_type_id
    ?? option.req_access_type_id
    ?? option.access_type_id
    ?? option.aiToolId
    ?? option.ai_tool_id;
};

const getRequestId = (row) => row?.id ?? row?.access_request_id ?? null;
const getAccessRequestId = (row) => row?.access_request_id ?? row?.id ?? null;
const isRequestClosed = (row) => {
  const status = row?.request_status
    ?? row?.request_status_name
    ?? row?.request_status_text
    ?? row?.requestStatus
    ?? row?.requestStatusName
    ?? row?.requestStatusText
    ?? row?.['Request Status'];
  const statusLabel = typeof status === 'object' && status !== null
    ? status.label ?? status.name ?? status.value
    : status;
  return String(statusLabel ?? '').trim()
    .toLowerCase() === 'closed';
};

const applyToolbarButtonClasses = (rootNode) => {
  if (!rootNode) return;

  const toolbarButtons = rootNode.querySelectorAll('.button-group button');

  toolbarButtons.forEach((buttonElement) => {
    const isResetButton = Boolean(buttonElement.querySelector('.pi-refresh'));
    const isFilterButton = buttonElement.classList.contains('table-utility-filter-btn');
    const isMoreButton = buttonElement.classList.contains('ai-access-req-more-btn');
    const isExportButton = buttonElement.classList.contains('ai-access-req-export-btn');
    const isAddButton = buttonElement.classList.contains('add-button');
    const isDownloadButton = !isFilterButton && !isResetButton && !isMoreButton
    && !isExportButton && !isAddButton && Boolean(buttonElement.querySelector('svg'));

    // Map conditions directly to target class names
    const classMap = {
      'ai-access-req-filter-btn': isFilterButton,
      'ai-access-req-reset-btn': isResetButton,
      'ai-access-req-export-btn': isExportButton,
      'ai-access-req-download-btn': isDownloadButton,
      'ai-access-req-add-btn': isAddButton,
      'ai-access-req-more-btn': isMoreButton,
    };

    // Apply or remove classes dynamically
    Object.entries(classMap).forEach(([className, shouldApply]) => {
      buttonElement.classList.toggle(className, shouldApply);
    });
  });
};

const extractModuleActions = (moduleName, permissions) => {
  if (!permissions) return [];

  // 1. If permissions is an object map: { 'resource:ai-access-request': ['view', 'edit'] }
  if (typeof permissions === 'object' && !Array.isArray(permissions)) {
    const modulePerms = permissions[moduleName];
    if (Array.isArray(modulePerms)) return modulePerms;
    if (typeof modulePerms === 'object' && modulePerms !== null) {
      return Object.keys(modulePerms).filter((key) => Boolean(modulePerms[key]));
    }
    return [];
  }

  // 2. If permissions is an array
  if (Array.isArray(permissions)) {
    // Array of module objects: [{ module: 'resource:ai-access-request', actions: ['view'] }]
    const matchedModule = permissions.find(
      (p) => p?.moduleName === moduleName || p?.module === moduleName || p?.name === moduleName,
    );
    if (matchedModule) {
      return matchedModule.actions || matchedModule.permissions || [];
    }

    // Scoped string array: ['resource:ai-access-request:view', 'resource:ai-access-request:edit']
    const prefix = `${moduleName}:`;
    const scopedActions = permissions
      .filter((p) => typeof p === 'string' && p.startsWith(prefix))
      .map((p) => p.slice(prefix.length));
    if (scopedActions.length > 0) return scopedActions;

    // Fallback: flat action array if already scoped to the module beforehand
    return permissions;
  }

  return [];
};

// --- PERMISSION CHECKER ---
export const hasPermission = (moduleName, permissions, action) => {
  const actions = extractModuleActions(moduleName, permissions).map((a) =>
    String(a).toLowerCase()
      .trim(),
  );
  const targetAction = String(action).toLowerCase()
    .trim();

  switch (targetAction) {
  case 'view':
    return actions.includes('view') || actions.includes('read');

  case 'approve':
    // Allows explicit 'approve' OR composite 'view' + ('update' / 'edit')
    return (
      actions.includes('approve')
        || ((actions.includes('view') || actions.includes('read'))
          && (actions.includes('update') || actions.includes('edit')))
    );

  case 'add':
  case 'create':
    return actions.includes('add') || actions.includes('create');

  case 'edit':
  case 'update':
    return actions.includes('update') || actions.includes('edit');
  case 'delete':
    return actions.includes('delete') || actions.includes('remove');

  default:
    return actions.includes(targetAction);
  }
};


const AiAccessReq = () => {
  const { userProfile } = useAuth();
  const tableContainerRef = useRef(null);
  const tableStateRef = useRef(null);
  const moreMenuRef = useRef(null);
  const hasLoadedDropDownOptionsRef = useRef(false);
  const unfilteredOptionsRef = useRef(null);
  const [isAddAiAccessReqOpen, setIsAddAiAccessReqOpen] = useState(false);
  const [isEditAiAccessReqOpen, setIsEditAiAccessReqOpen] = useState(false);
  const [isSubmittingAddRequest, setIsSubmittingAddRequest] = useState(false);
  const [isSubmittingEditRequest, setIsSubmittingEditRequest] = useState(false);
  const [isLoadingEditRequest, setIsLoadingEditRequest] = useState(false);
  const [editRequestRow, setEditRequestRow] = useState(null);
  const [editRequestData, setEditRequestData] = useState(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, rowData: null });
  const [approvalDialog, setApprovalDialog] = useState(null);
  const [dropDownOptions, setDropDownOptions] = useState({
    employee: [],
    project: [],
    accessRequestType: [],
    aiTool: [],
  });
  const [selectedEmployee, setSelectedEmployee] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
  });
  const permissions = userProfile?.permissions || [];

  // access control
  const canViewAiAccessRequest = hasPermission(MODULE_NAMES.RESOURCE_AI_ACCESS_REQUEST, permissions, 'view');
  const canApproveAiAccessRequest = hasPermission(MODULE_NAMES.RESOURCE_AI_ACCESS_REQUEST_APPROVAL, permissions, 'approve');
  const canAddAiAccessRequest = hasPermission(MODULE_NAMES.RESOURCE_AI_ACCESS_REQUEST, permissions, 'add');
  const canEditAiAccessRequest = hasPermission(MODULE_NAMES.RESOURCE_AI_ACCESS_REQUEST, permissions, 'edit');
  const canDeleteAiAccessRequest = hasPermission(MODULE_NAMES.RESOURCE_AI_ACCESS_REQUEST, permissions, 'delete');
  const canViewAiAccessRequestDashBoard = hasPermission(MODULE_NAMES.RESOURCE_AI_ACCESS_REQ_DASHBOARD, permissions, 'view');
  const canViewAiAccessRequestDashBoardSummary = hasPermission(MODULE_NAMES.RESOURCE_AI_ACCESS_REQ_DASHBOARD_SUMMARY, permissions, 'view');
  const canViewAiToolMis = hasPermission(MODULE_NAMES.MIS_AI_TOOL_MIS, permissions, 'view');


  const dropDownRequestOptions = useMemo(() => ({
    authToken: userProfile?.access_token || '',
    currentEmployeeId: userProfile?.employeeId || '',
    isSuperAdmin: userProfile?.roleDetails?.some(
      (role) => role?.roleName?.replace(/\s+/g, '').toLowerCase() === 'superadmin',
    ) || false,
    userInfo: userProfile || {},
    projectIds: [],
  }), [userProfile]);

  // Response handler — sync pagination from API response
  const handleFilteredApiResponse = useCallback((response) => {
    if (!response) return;

    const paginationData
      = response.data?.pagination
      || response.data?.paginationConfig
      || response.data?.data?.pagination
      || response.data?.data?.paginationConfig;

    const newPage = paginationData?.page ?? paginationData?.currentPage ?? 1;
    const newLimit = paginationData?.limit ?? PAGE_SIZE;
    const newTotal
      = paginationData?.total
      ?? paginationData?.count
      ?? response.meta?.total
      ?? response.data?.tableData?.length
      ?? response.data?.data?.tableData?.length
      ?? 0;

    setPagination((prev) => {
      if (prev.page === newPage && prev.limit === newLimit && prev.total === newTotal) {
        return prev;
      }
      return { page: newPage, limit: newLimit, total: newTotal };
    });
  }, []);

  // Pagination change handler
  const handlePaginationChange = useCallback((newPagination) => {
    setPagination(newPagination);
  }, []);

  const handleSetTableState = useCallback((getTableState) => {
    tableStateRef.current = getTableState;
  }, []);

  const triggerTableRefresh = useCallback(async () => {
    try {
      const tableState = tableStateRef.current?.();
      if (typeof tableState?.refreshTableData !== 'function') {
        throw new Error('The table refresh handler is unavailable.');
      }
      await tableState.refreshTableData();
    } catch (error) {
      console.error('Failed to refresh AI access requests:', error);
      toast.error('Unable to refresh AI access requests. Please reload the page.');
    }
  }, []);

  const refreshAiAccessRequests = useCallback(() => triggerTableRefresh(), [triggerTableRefresh]);

  const openDeleteConfirmDialog = useCallback((rowData) => {
    setDeleteConfirmDialog({ open: true, rowData });
  }, []);

  const closeDeleteConfirmDialog = useCallback(() => {
    setDeleteConfirmDialog({ open: false, rowData: null });
  }, []);

  // Reset to page 1 on filter/reset actions
  const resetPaginationToFirstPage = useCallback(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Toolbar button class
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    const timer = setTimeout(() => applyToolbarButtonClasses(container), 500);
    const toolbarObserver = new MutationObserver(() => applyToolbarButtonClasses(container));
    toolbarObserver.observe(container, { childList: true, subtree: true });
    return () => {
      clearTimeout(timer);
      toolbarObserver.disconnect();
    };
  }, []);

  const handleAddIconClick = useCallback(() => {
    setIsAddAiAccessReqOpen(true);
  }, []);

  const handleSelectionChange = useCallback(({ employees, project }) => {
    setSelectedEmployee(employees);
    setSelectedProject(project);
  }, []);

  const loadDropDownOptions = useCallback(async () => {
    try {
      const [employeeDropDownOptions, projectDropDownOptions, accessRequestTypeDropDownOptions, aiToolDropDownOptions] = await Promise.all([
        fetchAiAccessDropDownOptions({ ...dropDownRequestOptions, optionType: 'employees' }),
        fetchAiAccessDropDownOptions({ ...dropDownRequestOptions, optionType: 'projects' }),
        fetchAiAccessDropDownOptions({ ...dropDownRequestOptions, optionType: 'access_request_types' }),
        fetchAiAccessDropDownOptions({ ...dropDownRequestOptions, optionType: 'ai_tools' }),
      ]);
      const initialOptions = {
        employee: employeeDropDownOptions?.data?.data || [],
        project: projectDropDownOptions?.data?.data || [],
        accessRequestType: accessRequestTypeDropDownOptions?.data?.data || [],
        aiTool: aiToolDropDownOptions?.data?.data || [],
      };
      unfilteredOptionsRef.current = initialOptions;
      setDropDownOptions(initialOptions);
      hasLoadedDropDownOptionsRef.current = true;
      return initialOptions;
    } catch (error) {
      console.error('Failed to fetch dropdown options:', error);
      return null;
    }
  }, [dropDownRequestOptions]);

  const handleCellClick = useCallback(async (params) => {
    const { action, rowData } = params || {};
    if (rowData?.isTotalRow) return;
    try {
      const approval = getApprovalDialog(params || {});
      if (approval) {
        setApprovalDialog(approval);
        return;
      }
    } catch (error) {
      toast.error(error.message);
      return;
    }
    if (action === 'edit') {
      if (isRequestClosed(rowData)) return;

      setEditRequestRow(rowData);
      setEditRequestData(rowData);
      setIsEditAiAccessReqOpen(true);
      setIsLoadingEditRequest(true);

      const requestId = getRequestId(rowData);
      const optionsRequest = hasLoadedDropDownOptionsRef.current
        ? Promise.resolve(unfilteredOptionsRef.current)
        : loadDropDownOptions();

      try {
        const [detailResponse] = await Promise.all([
          requestId
            ? getAiAccessRequestById({ authToken: userProfile?.access_token || '', requestId })
            : Promise.resolve(null),
          optionsRequest,
        ]);
        const detailRecord = detailResponse?.data?.success
          ? (Array.isArray(detailResponse.data.data)
            ? detailResponse.data.data[0]
            : detailResponse.data.data)
          : null;
        if (detailRecord) setEditRequestData({ ...rowData, ...detailRecord });
      } catch (error) {
        console.error('Failed to load AI access request details:', error);
      } finally {
        setIsLoadingEditRequest(false);
      }
      return;
    }
    if (action === 'delete') {
      openDeleteConfirmDialog(rowData);
    }
  }, [loadDropDownOptions, openDeleteConfirmDialog, userProfile?.access_token]);

  const handleConfirmDelete = useCallback(async () => {
    const rowData = deleteConfirmDialog.rowData;
    closeDeleteConfirmDialog();

    const accessRequestId = getAccessRequestId(rowData);
    const currentUserEmpId = userProfile?.employeeId;
    const currentUserEmailId = userProfile?.email;
    if (accessRequestId === undefined || accessRequestId === null || accessRequestId === ''
      || !currentUserEmpId || !currentUserEmailId) {
      toast.error('Unable to delete AI access request: missing request or user details.');
      return;
    }

    try {
      const response = await deleteAiAccessRequest({
        authToken: userProfile?.access_token || '',
        accessRequestId,
        currentUserEmpId,
        currentUserEmailId,
      });
      if (response?.data instanceof Error || response?.data?.success === false
        || !response?.status || response.status >= 400) {
        throw response?.data instanceof Error
          ? response.data
          : new Error(response?.data?.message || response?.message || 'Failed to delete AI access request.');
      }
      toast.success(response?.data?.message || response?.message || 'AI access request deleted successfully.');
      await triggerTableRefresh();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to delete AI access request.');
    }
  }, [closeDeleteConfirmDialog, deleteConfirmDialog.rowData, triggerTableRefresh, userProfile]);

  useEffect(() => {
    if (!isAddAiAccessReqOpen) return;
    if (hasLoadedDropDownOptionsRef.current) return;
    loadDropDownOptions();
  }, [isAddAiAccessReqOpen, isEditAiAccessReqOpen, loadDropDownOptions]);

  useEffect(() => {
    if (!isAddAiAccessReqOpen) return undefined;

    const employees = (Array.isArray(selectedEmployee) ? selectedEmployee : [])
      .map((employee) => (typeof employee === 'object' ? employee?.value ?? employee?.id : employee))
      .filter((employee) => employee !== undefined && employee !== null && employee !== '');
    const project = typeof selectedProject === 'object'
      ? selectedProject?.value
        ?? selectedProject?.id
        ?? selectedProject?.projectId
        ?? selectedProject?.project_id
        ?? selectedProject?.project_seq_id
      : selectedProject;

    if (!employees.length && (project === null || project === undefined || project === '')) {
      if (unfilteredOptionsRef.current) {
        setDropDownOptions((currentOptions) => ({
          ...currentOptions,
          employee: unfilteredOptionsRef.current.employee,
          project: unfilteredOptionsRef.current.project,
        }));
      }
      return undefined;
    }

    let isCurrentRequest = true;
    const requestOptions = {
      ...dropDownRequestOptions,
      projectIds: project ? [project] : [],
    };

    const loadFilteredOptions = async () => {
      try {
        const [employeeResponse, projectResponse] = await Promise.all([
          fetchAiAccessDropDownOptions({ ...requestOptions, optionType: 'employees' }),
          fetchAiAccessDropDownOptions({ ...requestOptions, optionType: 'projects' }),
        ]);

        if (!isCurrentRequest) return;

        setDropDownOptions((currentOptions) => ({
          ...currentOptions,
          employee: employeeResponse?.data?.data || [],
          project: projectResponse?.data?.data || [],
        }));
      } catch (error) {
        console.error('Failed to fetch filtered dropdown options:', error);
      }
    };

    loadFilteredOptions();

    return () => {
      isCurrentRequest = false;
    };
  }, [
    isAddAiAccessReqOpen,
    selectedEmployee,
    selectedProject,
    dropDownRequestOptions,
  ]);

  const handleAddModalClose = useCallback(() => {
    setSelectedEmployee([]);
    setSelectedProject(null);
    if (unfilteredOptionsRef.current) {
      setDropDownOptions((currentOptions) => ({
        ...currentOptions,
        employee: unfilteredOptionsRef.current.employee,
        project: unfilteredOptionsRef.current.project,
      }));
    }
    setIsAddAiAccessReqOpen(false);
  }, []);

  // Listen for reset/filter-apply clicks to reset pagination
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const handleResetOrFilterClick = (event) => {
      const isReset = event.target.closest('.ai-access-req-reset-btn');
      const isFilterApply = event.target.closest('.tu-filter-footer-apply');
      const isFilterReset = event.target.closest('.tu-filter-footer-reset');

      if ((isReset || isFilterApply || isFilterReset) && container.contains(event.target)) {
        resetPaginationToFirstPage();
      }
    };

    container.addEventListener('click', handleResetOrFilterClick, true);
    return () => container.removeEventListener('click', handleResetOrFilterClick, true);
  }, [resetPaginationToFirstPage]);

  const handleAddAiAccessRequest = useCallback(async ({
    employees = [],
    requestType,
    aiTools = [],
    project,
    businessJustification = '',
  }) => {
    const employeeIds = (Array.isArray(employees) ? employees : [employees])
      .map(getOptionId)
      .filter((id) => id !== undefined && id !== null && id !== '');
    const aiToolIds = (Array.isArray(aiTools) ? aiTools : [aiTools])
      .map(getOptionId)
      .filter((id) => id !== undefined && id !== null && id !== '');
    const projectId = getOptionId(project);
    const reqAccessTypeId = getOptionId(requestType);
    const justification = businessJustification.trim();
    const hasId = (id) => id !== undefined && id !== null && id !== '';

    if (!employeeIds.length || !hasId(reqAccessTypeId) || !aiToolIds.length || !hasId(projectId) || !justification) {
      toast.error('Please complete all required fields.');
      return;
    }

    setIsSubmittingAddRequest(true);
    try {
      const response = await addAiAccessRequest({
        authToken: userProfile?.access_token || '',
        currentUserEmployeeId: userProfile?.employeeId || '',
        employeesIds: employeeIds,
        businessJustification: justification,
        reqAccessTypeId,
        projectId,
        aiToolIds,
      });
      if (response?.data?.success === false || response?.data instanceof Error) {
        throw response.data;
      }
      toast.success(response?.data?.message || response?.message || 'AI access request submitted successfully.');
      handleAddModalClose();
      await refreshAiAccessRequests();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to submit AI access request.');
    } finally {
      setIsSubmittingAddRequest(false);
    }
  }, [userProfile, handleAddModalClose, refreshAiAccessRequests]);

  const handleEditAiAccessRequest = useCallback(async ({
    employee,
    project,
    requestType,
    aiTools = [],
    businessJustification = '',
  }) => {
    const aiToolIds = (Array.isArray(aiTools) ? aiTools : [aiTools])
      .map(getOptionId)
      .filter((id) => id !== undefined && id !== null && id !== '');
    const requestId = getRequestId(editRequestData) ?? getRequestId(editRequestRow);
    const employeeId = getOptionId(employee);
    const projectId = getOptionId(project);
    const requesterEmployeeId = userProfile?.employeeId;
    const reqAccessTypeId = getOptionId(requestType);
    const justification = businessJustification.trim();
    const hasId = (id) => id !== undefined && id !== null && id !== '';

    if (!hasId(requestId) || !hasId(employeeId) || !hasId(projectId)
      || !hasId(requesterEmployeeId) || !hasId(reqAccessTypeId) || !aiToolIds.length || !justification) {
      toast.error('Please complete all required fields.');
      return;
    }

    setIsSubmittingEditRequest(true);
    try {
      console.log('Updating AI access request with data:', {
        userProfile,
      });
      const response = await updateAiAccessRequest({
        authToken: userProfile?.access_token || '',
        requestId,
        employeeEmailId: userProfile?.email || userProfile?.userInfo?.email || '',
        employeeId,
        projectId,
        roleDetails: userProfile?.roleDetails || userProfile?.userInfo?.roleDetails || [],
        requesterEmployeeId,
        businessJustification: justification,
        reqAccessTypeId,
        aiToolIds,
      });
      if (response?.data?.success === false || response?.data instanceof Error) {
        throw response.data;
      }
      toast.success(response?.data?.message || 'AI access request updated successfully.');
      setIsEditAiAccessReqOpen(false);
      setEditRequestRow(null);
      setEditRequestData(null);
      await refreshAiAccessRequests();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update AI access request.');
    } finally {
      setIsSubmittingEditRequest(false);
    }
  }, [editRequestData, editRequestRow, userProfile, refreshAiAccessRequests]);

  const handleEditModalClose = useCallback(() => {
    setIsEditAiAccessReqOpen(false);
    setEditRequestRow(null);
    setEditRequestData(null);
  }, []);

  const openAiAccessRequestDashBoardInNewTab = () => {
    // Replace with your actual route path (e.g., '/resource/ai-access-request')
    const route = '/resource/ai-access-request-dashboard';
    window.open(route, '_blank', 'noopener,noreferrer');
  };
  const openAiToolMisInNewTab = () => {
    // Replace with your actual route path (e.g., '/resource/ai-tool-mis')
    const route = '/mis/ai-tool-mis';
    window.open(route, '_blank', 'noopener,noreferrer');
  };

  const moreMenuItems = useMemo(
    () =>
      [
        // Parentheses ensure "at least one" condition is evaluated first
        (canViewAiAccessRequestDashBoard || canViewAiAccessRequestDashBoardSummary) && {
          label: 'AI Usage Dashboard',
          className: 'active-menu-item',
          command: openAiAccessRequestDashBoardInNewTab,
        },
        (canViewAiToolMis) && {
          label: 'AI Tool MIS',
          className: 'ai-tool-mis-menu-item',
          command: openAiToolMisInNewTab,
        },
      ].filter(Boolean), // Removes any false or undefined items
    [
      canViewAiAccessRequest,
      canViewAiAccessRequestDashBoard,
      canViewAiAccessRequestDashBoardSummary,
      canViewAiToolMis,
      openAiAccessRequestDashBoardInNewTab,
      openAiToolMisInNewTab,
    ],
  );


  // Additional elements like more menu
  const additionalElements = useMemo(() => [
    <React.Fragment key="ai-access-req-more-menu">
      <Menu
        ref={moreMenuRef}
        model={moreMenuItems}
        popup
        appendTo={document.body}
        className="ai-access-req-more-menu"
      />
      <Button
        icon="pi pi-ellipsis-v"
        className="p-button-text ai-access-req-more-btn"
        onClick={(e) => moreMenuRef.current?.toggle(e)}
        tooltip="More options"
        tooltipOptions={{ position: 'top', className: 'ai-access-req-more-tooltip' }}
      />
    </React.Fragment>,
  ], [moreMenuItems]);


  // Static configuration
  const configuration = useMemo(
    () => ({
      generalSettings: {
        enableApiFiltering: true,
        baseURL,
        headers: { token: userProfile?.access_token || '' },
        additionalPayloadData: {
          employeeId: userProfile?.employeeId || '',
        },
        userInfo: {
          id: userProfile?.id || '',
          emailId: userProfile?.email || '',
          permissions: userProfile?.permissions || [],
          roleDetails: userProfile?.roleDetails || [],
          employeeId: userProfile?.employeeId || '',
        },
        handlers: { handleAddIconClick },
        handler: {
          handleFilteredApiResponse,
          //   onFilterUpdate: handleFilterUpdate, // Directly handled via API here
          onCellClickHandler: handleCellClick,
          setTableStateHandler: handleSetTableState,
        },
        apiEndpoints: {
          getAllViews: { endpoint: '', enabled: false },
          getFilteredData: { endpoint: '', enabled: true },
          saveOrUpdateView: { endpoint: '', enabled: false },
          deleteView: { endpoint: '', enabled: false },
          download: {
            endpoint: 'export',
            enabled: Boolean(canViewAiAccessRequest),
            filename: () => buildExportFileName('AI-Access-Request'),
            sheetName: () => buildExportSheetName('AI-Access-Request'),
          },
        },
        showToolBar: true,
        showPagination: true,
        toolBarSettings: {
          showAddNew: canAddAiAccessRequest,
          showFilterOption: true,
          showExpandCollapse: false,
          showDownloadExcel: true,
          downloadExcelSheetName: 'AI Access Request',
          showColumnCustomize: false,
          showHelp: false,
          showSearch: false,
          showSaveView: false,
          showToolBarHeading: true,
          toolBarHeading: 'AI Access Request',
          showReset: false,
          additionalElements: additionalElements,
        },
      },
      showFilterOption: true,
      tableSettings: {
        enableTreeTable: false,
        size: 'large',
        loaderSkeleton: { rows: 10, bodyCells: 1, columns: 8 },
        pageSize: PAGE_SIZE,
        paginationConfig: {
          ...pagination,
          onPaginationChange: handlePaginationChange,
          showPageSizeSelector: false,
        },
        showPageSizeSelector: false,
        maxBreakdowns: 5,
        showExpandCollapse: false,
        tableScrollHeight: 'calc(100vh - 200px)',
        maxFrozenColumns: { desktop: 4, tablet: 2, mobile: 1 },
      },
    }),
    [
      userProfile, pagination, handleFilteredApiResponse,
      handlePaginationChange, handleCellClick, handleSetTableState,
    ],
  );

  if (!canViewAiAccessRequest) {
    return <div>You do not have permission to view AI access request.</div>;
  }

  return (
    <div className="ai-access-req-outer">
      <div className="ai-access-req-table">
        <div className="ai-access-req-table-utility" ref={tableContainerRef}>
          <TableUtility configuration={configuration} />
        </div>
      </div>

      {canApproveAiAccessRequest && approvalDialog && (
        <ApprovalStatusModal
          requestType={approvalDialog.requestType}
          approvalLevel={approvalDialog.approvalLevel}
          onClose={() => setApprovalDialog(null)}
          requestId={getRequestId(approvalDialog.rowData)}
          userProfile={userProfile}
          rowData={approvalDialog.rowData}
          onSuccess={() => {
            setApprovalDialog(null);
            triggerTableRefresh();
          }}
        />
      )}

      <ActionConfirmModal
        open={Boolean(canDeleteAiAccessRequest && deleteConfirmDialog?.open)}
        className="ai-access-req-action-confirm"
        currentAction="delete"
        title="Delete AI Access Request"
        message="Do you want to delete this AI access request?"
        confirmText="Delete"
        cancelText="Cancel"
        onCancel={closeDeleteConfirmDialog}
        onConfirm={handleConfirmDelete}
      />

      {canAddAiAccessRequest && isAddAiAccessReqOpen && (
        <AddAiAccessReqModal
          open={isAddAiAccessReqOpen}
          onSubmit={handleAddAiAccessRequest}
          isSubmitting={isSubmittingAddRequest}
          dropDownOptions={dropDownOptions}
          onClose={handleAddModalClose}
          onSelectionsChange={handleSelectionChange}
        />
      )}

      {canEditAiAccessRequest && isEditAiAccessReqOpen && (
        <EditAiAccessReqModal
          open={isEditAiAccessReqOpen}
          initialData={editRequestData}
          dropDownOptions={dropDownOptions}
          isLoading={isLoadingEditRequest}
          isSubmitting={isSubmittingEditRequest}
          onSubmit={handleEditAiAccessRequest}
          onClose={handleEditModalClose}
        />
      )}
    </div>
  );
};
export default AiAccessReq;
